import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useState } from 'react';

import { apiGet, getToken } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { ErrorState, LoadingState } from '@/components/ui/States';

interface SlabRow {
  rate: number;
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  total: number;
}

interface GstReport {
  period: string;
  taxable_total: number;
  tax_total: number;
  cgst_total: number;
  sgst_total: number;
  igst_total: number;
  slabs?: SlabRow[];
}

const PERIODS: { id: string; label: string }[] = [
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'this_quarter', label: 'This quarter' },
  { id: 'this_year', label: 'This year' },
];

/**
 * GST summary — the report your CA actually opens. We deliberately
 * keep the layout boring: a row per slab with CGST/SGST/IGST so it
 * looks identical to a GSTR-1 worksheet they're used to.
 */
export function GstReportPage() {
  const [period, setPeriod] = useState('this_month');

  const query = useQuery({
    queryKey: ['report', 'gst', period],
    queryFn: () => apiGet<GstReport>(endpoints.reportGst, { period }),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Couldn't load GST"
        description={(query.error as Error)?.message}
        onRetry={() => query.refetch()}
      />
    );
  }
  const r = query.data;
  const slabs = r.slabs ?? [];

  const onExport = () => {
    const token = getToken() ?? '';
    const url = `${endpoints.reportExport}?type=gst&period=${period}&token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <>
      <PageHeader
        back
        title="GST summary"
        description="Numbers your CA can paste straight into GSTR-1."
        actions={
          <Button
            variant="outline"
            iconLeft={<Download className="h-4 w-4" />}
            onClick={onExport}
          >
            Export CSV
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-5">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            className={
              'h-9 px-4 rounded-full text-sm font-medium transition-colors ' +
              (period === p.id
                ? 'bg-brand-600 text-white'
                : 'bg-surface border border-line text-ink-soft hover:text-ink hover:bg-surface-2')
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <StatCard label="Taxable value" value={<Money value={r.taxable_total} />} />
        <StatCard label="Total tax" value={<Money value={r.tax_total} />} />
        <StatCard label="CGST + SGST" value={<Money value={(r.cgst_total ?? 0) + (r.sgst_total ?? 0)} />} />
        <StatCard label="IGST" value={<Money value={r.igst_total ?? 0} />} />
      </div>

      <Card padded={false}>
        <div className="px-5 py-4 border-b border-line">
          <h2 className="text-base font-semibold">Per-slab breakdown</h2>
        </div>
        {slabs.length === 0 ? (
          <div className="px-5 py-12 text-center text-ink-soft">
            No GST bills in this period.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr className="text-left text-xs uppercase tracking-wide text-ink-soft">
                  <th className="px-5 py-2">Slab</th>
                  <th className="px-5 py-2 text-right">Taxable</th>
                  <th className="px-5 py-2 text-right">CGST</th>
                  <th className="px-5 py-2 text-right">SGST</th>
                  <th className="px-5 py-2 text-right">IGST</th>
                  <th className="px-5 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {slabs.map((s) => (
                  <tr key={s.rate} className="border-t border-line">
                    <td className="px-5 py-3 font-medium">{s.rate}%</td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      <Money value={s.taxable} />
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      <Money value={s.cgst} />
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      <Money value={s.sgst} />
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      <Money value={s.igst} />
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold">
                      <Money value={s.total} />
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-line bg-surface-2">
                  <td className="px-5 py-3 font-semibold">Total</td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold">
                    <Money value={r.taxable_total} />
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold">
                    <Money value={r.cgst_total ?? 0} />
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold">
                    <Money value={r.sgst_total ?? 0} />
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold">
                    <Money value={r.igst_total ?? 0} />
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-bold text-base">
                    <Money value={r.taxable_total + r.tax_total} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
